export type NativeImageJobStatus =
  | 'QUEUED'
  | 'PROMPTING'
  | 'GENERATING'
  | 'VALIDATING'
  | 'SLUGGING'
  | 'UPLOADING'
  | 'VERIFYING'
  | 'WRITING_NOCODB'
  | 'COMPLETED'
  | 'FAILED';

export type NativeImageGenerationStatus = {
  run: {
    id: string;
    articleId: string;
    status: 'QUEUED' | 'PREPARING_PROMPTS' | 'RUNNING' | 'COMPLETED' | 'COMPLETED_WITH_ERRORS' | 'FAILED';
    expectedCount: number;
    completedCount: number;
    failedCount: number;
    createdAt: string;
    updatedAt: string;
    finishedAt?: string | null;
    reconciliationPending: boolean;
    reconciliationAttempt: number;
    reconciliationError?: string | null;
  };
  jobs: Array<{
    id: string;
    slotKey: string;
    slotType: 'FEATURED' | 'STACK';
    stackId?: string | null;
    sectionId?: string | null;
    variationIndex: number;
    status: NativeImageJobStatus;
    attempt: number;
    maxAttempts: number;
    retryGeneration: number;
    prompt?: string | null;
    publicUrl?: string | null;
    lastError?: string | null;
    dispatchState: 'RECOVERING' | 'WAITING' | null;
  }>;
};

type ApiPayload<T> = { data?: T; error?: string; code?: string; details?: string };

const parsePayload = async <T>(response: Response): Promise<ApiPayload<T>> => {
  try {
    return (await response.json()) as ApiPayload<T>;
  } catch {
    return {};
  }
};

export class NativeImageGenerationService {
  static async start(articleId: string): Promise<
    | { backend: 'native'; runId: string; expectedCount: number; status: string }
    | { backend: 'disabled' }
  > {
    const response = await fetch('/api/images/initial/start', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ articleId }),
    });
    const payload = await parsePayload<{
      backend: 'native';
      runId: string;
      expectedCount: number;
      status: string;
    }>(response);
    if (response.status === 409 && payload.code === 'NATIVE_BACKEND_DISABLED') return { backend: 'disabled' };
    if (!response.ok || !payload.data) {
      throw new Error(payload.details || payload.error || `Native image start failed (${response.status})`);
    }
    return payload.data;
  }

  static async getStatus(articleId: string): Promise<NativeImageGenerationStatus | null> {
    const response = await fetch(`/api/images/initial/status?articleId=${encodeURIComponent(articleId)}`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (response.status === 404) return null;
    const payload = await parsePayload<NativeImageGenerationStatus>(response);
    if (response.status === 409 && payload.code === 'NATIVE_BACKEND_DISABLED') return null;
    if (!response.ok || !payload.data) throw new Error(payload.error || `Image status failed (${response.status})`);
    return payload.data;
  }

  static async suggestLessSuggestive(jobId: string): Promise<{prompt: string; originalPrompt: string}> {
    const response = await fetch('/api/images/initial/less-suggestive', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({jobId}),
    });
    const payload = await response.json();
    if (!response.ok || typeof payload.prompt !== 'string' || typeof payload.originalPrompt !== 'string') throw new Error(payload.error || 'Could not rewrite prompt');
    return payload;
  }

  static async retry(jobId: string, promptOverride?: { originalPrompt: string; prompt: string }): Promise<void> {
    const response = await fetch('/api/images/initial/retry', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jobId, promptOverride }),
    });
    if (!response.ok) {
      const payload = await parsePayload<never>(response);
      throw new Error(payload.error || `Image retry failed (${response.status})`);
    }
  }
}
